import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RootState } from '../..';
import { IUser } from '../../UserSlice';
import { ICourse } from '../CoursesSlice';
import { getAuthUser } from '../../AuthSlice/AuthApi';

export const CHAT_CONNECTION_MODE = {
  USER: 'user',
  ADMIN: 'admin',
} as const;

export type TMessageStatus = 'SAVED' | 'DELIVERED' | 'SEEN';
export type TChatConnectionMode = typeof CHAT_CONNECTION_MODE[keyof typeof CHAT_CONNECTION_MODE];
export interface IMessageStatus {
  user: string;
  status: TMessageStatus;
}
export type TChatFile = {
  name: string;
  url: string;
};

export interface IMessage {
  _id: string;
  conversation: string | IConversation;
  message: string;
  sender: IUser;
  createdAt: string;
  status: Array<IMessageStatus>;
  files?: Array<TChatFile>;
}

interface ISendMessage {
  conversation: string;
  message: string;
  files?: Array<TChatFile>;
}

interface IGetConversations {
  course: string;
}

interface IStartConversation {
  course: string;
  users: string[];
}

interface IGetNotifications {
  unSeenCount: number;
  notificationListData: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    docs: Array<IChatNotification>;
  };
}

interface IGetMessages {
  conversation: string;
  limit: number;
  offset: number;
}

interface ISyncMessages {
  ids: Array<string>;
  status: TMessageStatus;
}

export interface IConversationUser {
  _id: string;
  firstname: string;
  lastname: string;
  avatar: string;
  isOnline?: boolean;
  lastSeen?: string;
}

export interface IConversation {
  _id: string;
  course: string | ICourse;
  users: string[] | IConversationUser[];
  unSeenCount?: number;
  lastMessageTime?: string;
}

export interface IChatConnection {
  user: string;
  isOnline: boolean;
  lastSeen: string;
}

export interface IChatNotification {
  _id: string;
  unseenCount: number;
  lastMessageSentAt?: string;
  course: ICourse;
  lastMessage?: {
    _id: string;
    message: string;
    sender: {
      _id: string;
      firstname: string;
      lastname?: string;
      avatar?: string;
    };
    files?: Array<TChatFile>;
  };
}

export interface IChatState {
  chatConnectionMode?: TChatConnectionMode;
  initiated: boolean;
  courseId?: string;
  authUserId?: string;
  isConnected: boolean;
  isLoading: boolean;
  isNewChat: boolean;
  messagesData?: {
    messages: IMessage[];
    limit: number;
    offset: number;
    totalDocs: number;
  };
  conversations: IConversation[];
  selectedConversation: IConversation | null;
  notificationData: {
    isLoadingMore?: boolean;
    unSeenCount: number;
    notificationListData?: {
      limit?: number;
      offset?: number;
      totalDocs?: number;
      list: Array<IChatNotification>;
    };
  };
  isConversationCreating: boolean;
  conversationTypingData: Array<{
    conversationId: string;
    typing: boolean;
    userId: string;
    user: {
      _id: string;
      firstname: string;
      lastname?: string;
      avatar?: string;
    };
  }>;
  isConversationsFetched: boolean;
}

const initialState: IChatState = {
  initiated: false,
  authUserId: undefined,
  isConnected: false,
  chatConnectionMode: undefined,
  isLoading: false,
  isNewChat: false,
  messagesData: undefined,
  conversations: [],
  selectedConversation: null,
  notificationData: {
    unSeenCount: 0,
    notificationListData: undefined,
  },
  isConversationCreating: false,
  conversationTypingData: [],
  isConversationsFetched: false,
};

export const ChatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setInitiated: (state, action: PayloadAction<boolean>) => {
      state.initiated = action.payload;
    },
    setConnected: (state, action: PayloadAction<boolean>) => {
      state.isConnected = action.payload;
    },
    addMessage: (state, action: PayloadAction<IMessage>) => {
      const conversation = action.payload.conversation as IConversation;

      if (
        state.selectedConversation?._id === conversation._id &&
        state.messagesData
      ) {
        state.messagesData.messages.push(action.payload);
        state.messagesData.totalDocs += 1;
      }
    },
    setMessages: (
      state,
      action: PayloadAction<{
        messages: IMessage[];
        limit: number;
        offset: number;
        totalDocs: number;
      }>
    ) => {
      state.isLoading = false;
      state.messagesData = action.payload;
    },
    setSyncMessages: (state, action: PayloadAction<IMessage[]>) => {
      const newMessagesMap = new Map(
        action.payload.map((msg) => [msg._id, msg])
      );

      if (state.messagesData) {
        state.messagesData = {
          ...state.messagesData,
          messages: state.messagesData.messages.map((message) => {
            if (newMessagesMap.has(message._id)) {
              return { ...message, ...newMessagesMap.get(message._id) };
            }

            return message;
          }),
        };
      }
    },
    updateConversationsOnMessageReceived: (
      state,
      action: PayloadAction<IMessage>
    ) => {
      const message = action.payload;
      const messageSender = message.sender;
      const conversation = message.conversation as IConversation;
      const conversationUsers = conversation.users as IConversationUser[];
      const conversationCourse = conversation.course as ICourse;

      const newConversations = [...state.conversations];

      // Update conversation unseen count
      if (conversationCourse._id === state.courseId) {
        if (state.authUserId !== messageSender._id) {
          if (state.conversations.length) {
            const index = newConversations.findIndex(
              (item) => item._id === conversation._id
            );

            const _matchingItem = { ...newConversations[index] };

            _matchingItem.unSeenCount = (_matchingItem.unSeenCount || 0) + 1;
            _matchingItem.lastMessageTime = message.createdAt;

            newConversations[index] = { ..._matchingItem };
          }

          // Add new Conversation
          const isExist = newConversations.some(
            (item) => item._id === conversation._id
          );

          if (!isExist) {
            const newConversation: IConversation = {
              _id: conversation._id,
              course: conversationCourse._id,
              users: [...conversationUsers],
              lastMessageTime: conversation.lastMessageTime,
              unSeenCount: 0,
            };

            if (state.authUserId !== messageSender._id) {
              newConversation.unSeenCount = 1;
            }

            newConversations.push(newConversation);
          }
        } else if (state.conversations.length) {
          const index = newConversations.findIndex(
            (item) => item._id === conversation._id
          );

          if (index !== -1) {
            newConversations[index] = {
              ...newConversations[index],
              lastMessageTime: message.createdAt,
            };
          }
        }

        state.conversations = [...newConversations];
      }
    },
    setConversations: (state, action: PayloadAction<IConversation[]>) => {
      state.isLoading = false;
      state.conversations = action.payload;
      state.isConversationsFetched = true;
    },
    onNewConversationCreated: (state, action: PayloadAction<IConversation>) => {
      state.isConversationCreating = false;

      const conversation = action.payload;
      const newConversations = [...(state.conversations || [])];
      const isAlreadyExist = newConversations.some(
        (item) => item._id === conversation._id
      );

      if (!isAlreadyExist) {
        state.conversations = [
          ...newConversations,
          { ...conversation, unSeenCount: 0 },
        ];
      }
    },
    updateReceivedConversationTypingData: (
      state,
      action: PayloadAction<{
        conversationId: string;
        typing: boolean;
        userId: string;
        user: {
          _id: string;
          firstname: string;
          lastname?: string;
          avatar?: string;
        };
      }>
    ) => {
      const result = action.payload;

      let newConversationTypingData = [...state.conversationTypingData];

      if (result) {
        if (result.typing) {
          const index = newConversationTypingData.findIndex(
            (item) =>
              item.conversationId === result.conversationId &&
              item.userId === result.userId
          );

          if (index > -1) {
            newConversationTypingData[index] = {
              ...result,
            };
          } else {
            newConversationTypingData.push({ ...result });
          }
        } else {
          newConversationTypingData = [...newConversationTypingData].filter(
            (item) => {
              if (
                item.conversationId === result.conversationId &&
                item.userId === result.userId
              ) {
                return false;
              }

              return true;
            }
          );
        }
      }

      state.conversationTypingData = [...newConversationTypingData];
    },
    selectConversation: (state, action: PayloadAction<IConversation>) => {
      state.isLoading = false;
      state.selectedConversation = action.payload;
      state.isConversationCreating = false;
    },
    removeConversation: (state) => {
      state.selectedConversation = null;
      state.messagesData = undefined;
    },
    setUserStatus: (state, action: PayloadAction<IChatConnection>) => {
      const { user, isOnline, lastSeen } = action.payload;

      if (state.conversations.length > 0) {
        state.conversations = [...state.conversations].map((conversation) => {
          const _conversationUsers = conversation.users as IConversationUser[];

          return {
            ...conversation,
            users: [..._conversationUsers].map((u) =>
              u._id === user ? { ...u, isOnline, lastSeen } : u
            ),
          };
        });
      }

      if (state.selectedConversation) {
        const _conversationUsers = state.selectedConversation
          .users as IConversationUser[];

        state.selectedConversation = {
          ...state.selectedConversation,
          users: [..._conversationUsers].map((u) =>
            u._id === user ? { ...u, isOnline, lastSeen } : u
          ),
        };
      }
    },
    setUserNotificationData: (
      state,
      action: PayloadAction<IGetNotifications>
    ) => {
      const result = action.payload;

      state.notificationData = {
        unSeenCount: result.unSeenCount,
        notificationListData: {
          totalDocs: result.notificationListData.totalDocs || 0,
          offset: result.notificationListData.offset || 0,
          limit: result.notificationListData.limit || 0,
          list: result.notificationListData.docs,
        },
      };
    },
    updateNotificationDataOnMessageReceived: (
      state,
      action: PayloadAction<IMessage>
    ) => {
      const result = action.payload;
      const messageSender = result.sender;
      const messageConversation = result.conversation as IConversation;
      const conversationCourse = messageConversation.course as ICourse;
      const newNotificationData = { ...state.notificationData };
      const newConversationTypingData = [
        ...state.conversationTypingData,
      ].filter(
        (isTemplateExpression) =>
          isTemplateExpression.conversationId !== messageConversation._id
      );

      if (newNotificationData.notificationListData) {
        const newList = [
          ...(newNotificationData.notificationListData.list || []),
        ];

        const index = newList.findIndex(
          (item) => item._id === messageConversation._id
        );

        if (index > -1) {
          const matchingItem = { ...newList[index] };

          let unseenCount = matchingItem.unseenCount || 0;

          if (state.authUserId !== messageSender._id) {
            unseenCount += 1;
            newNotificationData.unSeenCount += 1;
          }

          newList[index] = {
            ...matchingItem,
            unseenCount,
            lastMessageSentAt: result.createdAt,
            lastMessage: {
              _id: result._id,
              message: result.message,
              sender: {
                _id: messageSender._id,
                firstname: messageSender.firstname,
                lastname: messageSender.lastname,
                avatar: messageSender.avatar,
              },
              files: result.files,
            },
          };
        } else {
          const newNotification = {
            _id: messageConversation._id,
            unseenCount: 0,
            course: { ...conversationCourse },
            lastMessageSentAt: result.createdAt,
            lastMessage: {
              _id: result._id,
              message: result.message,
              sender: {
                _id: messageSender._id,
                firstname: messageSender.firstname,
                lastname: messageSender.lastname,
                avatar: messageSender.avatar,
              },
              files: result.files,
            },
          };

          if (state.authUserId !== messageSender._id) {
            newNotification.unseenCount = 1;
            newNotificationData.unSeenCount += 1;
          }

          newList.unshift(newNotification);
        }

        newNotificationData.notificationListData.list = [...newList];
      }

      state.notificationData = { ...newNotificationData };
      state.conversationTypingData = [...newConversationTypingData];
    },
    updateNotificationDataOnSeenMessages: (
      state,
      action: PayloadAction<IMessage[]>
    ) => {
      const result = action.payload;

      const newNotificationData = { ...state.notificationData };
      const newConversations = [...state.conversations];
      const newNotificationList =
        newNotificationData.notificationListData &&
        newNotificationData.notificationListData.list
          ? [...newNotificationData.notificationListData.list]
          : [];

      result.forEach((messageItem) => {
        const statuses = messageItem.status;
        const conversationId =
          typeof messageItem.conversation === 'string'
            ? messageItem.conversation
            : (messageItem.conversation as IConversation)._id;

        const isSeen = statuses.some(
          (item) => item.status === 'SEEN' && item.user === state.authUserId
        );

        if (isSeen) {
          const _index = newNotificationList.findIndex(
            (item) => conversationId === item._id
          );

          if (_index > -1) {
            const _matchingItem: IChatNotification = {
              ...newNotificationList[_index],
            };

            // CAN BE CALLED TWICE CHECK
            if (_matchingItem.unseenCount && newNotificationData.unSeenCount) {
              _matchingItem.unseenCount -= 1;

              newNotificationList[_index] = { ..._matchingItem };

              newNotificationData.unSeenCount -= 1;
            }

            // Update conversation unseencount
            const conversationIndex = newConversations.findIndex(
              (item) => item._id === conversationId
            );

            if (conversationIndex > -1) {
              const _matchingConversation = {
                ...newConversations[conversationIndex],
              };

              if (_matchingConversation && _matchingConversation.unSeenCount) {
                _matchingConversation.unSeenCount -= 1;
              }

              newConversations[conversationIndex] = {
                ..._matchingConversation,
              };
            }
          }
        }
      });

      if (newNotificationData.notificationListData) {
        newNotificationData.notificationListData.list = [
          ...newNotificationList,
        ];
      }

      state.notificationData = { ...newNotificationData };

      state.conversations = [...newConversations];
    },
    toggleNewChat: (state) => {
      if (!state.isNewChat) {
        state.selectedConversation = null;
      }

      state.isNewChat = !state.isNewChat;
    },
    chatConnectSocket: (state) => {
      // This action is intended for middleware to handle
    },
    chatDisconnectSocket: (
      state,
      action: PayloadAction<boolean | undefined>
    ) => {
      state.isConnected = false;
    },
    sendMessage: (state, action: PayloadAction<ISendMessage>) => {
      // This action is intended for middleware to handle
    },
    getConversations: (state, action: PayloadAction<IGetConversations>) => {
      state.isLoading = true;
      state.courseId = action.payload.course;
    },
    getMessages: (state, action: PayloadAction<IGetMessages>) => {
      state.isLoading = true;
    },
    startConversation: (state, action: PayloadAction<IStartConversation>) => {
      state.isConversationCreating = true;
      // This action is intended for middleware to handle
    },
    syncMessages: (state, action: PayloadAction<ISyncMessages>) => {
      // This action is intended for middleware to handle
    },
    sendConversationTypingData: (
      state,
      action: PayloadAction<{ conversationId: string; typing: boolean }>
    ) => {
      // This action is intended for middleware to handle
    },
    onUserChatDisconnected: (state) => {
      Object.assign(state, initialState);
    },
    initiChatConnection: () => {
      // On Action Triggered
    },
    verifyChatConnection: (
      state,
      action: PayloadAction<{ isAdminModeEnabled: boolean }>
    ) => {
      // On Action Triggered
    },
    chatLoadMoreNotificationData: (
      state,
      action: PayloadAction<{ limit: number; offset: number }>
    ) => {
      // On Action Triggered
      if (state.notificationData) {
        state.notificationData = {
          ...state.notificationData,
          isLoadingMore: true,
        };
      }
    },
    setChatLoadMoreNotificationData: (
      state,
      action: PayloadAction<{
        limit?: number;
        offset?: number;
        totalDocs?: number;
        docs: Array<IChatNotification>;
      }>
    ) => {
      const result = action.payload;

      const newNotificationData = {
        ...state.notificationData,
      };

      if (newNotificationData.notificationListData) {
        const newNotificationListData = {
          ...newNotificationData.notificationListData,
        };

        newNotificationListData.limit = result.limit;
        newNotificationListData.offset = result.offset;
        newNotificationListData.list = [...newNotificationListData.list].concat(
          result.docs
        );

        newNotificationData.notificationListData = newNotificationListData;
      }

      newNotificationData.isLoadingMore = undefined;

      state.notificationData = newNotificationData;
    },
    setChatConnectionMode: (
      state,
      action: PayloadAction<{ isAdminModeEnabled: boolean } | undefined>
    ) => {
      let newChatConnectionMode: TChatConnectionMode | undefined;

      if (action.payload && action.payload.isAdminModeEnabled) {
        newChatConnectionMode = CHAT_CONNECTION_MODE.ADMIN;
      } else if (action.payload && !action.payload.isAdminModeEnabled) {
        newChatConnectionMode = CHAT_CONNECTION_MODE.USER;
      }

      state.chatConnectionMode = newChatConnectionMode;
    },

    resetCourseChatData: (state) => {
      state.isLoading = false;
      state.isNewChat = false;
      state.messagesData = undefined;
      state.conversations = [];
      state.selectedConversation = null;
      state.isConversationCreating = false;
      state.conversationTypingData = [];
      state.courseId = undefined;
      state.isConversationsFetched = false;
    },
  },
  extraReducers(builder) {
    builder.addCase(getAuthUser.fulfilled, (state, action) => {
      const result = action.payload;

      if (result) {
        state.authUserId = result._id;
      }
    });
  },
});

export const {
  setConnected,
  addMessage,
  setConversations,
  setMessages,
  chatConnectSocket,
  chatDisconnectSocket,
  sendMessage,
  getConversations,
  getMessages,
  startConversation,
  selectConversation,
  setUserStatus,
  removeConversation,
  toggleNewChat,
  syncMessages,
  setSyncMessages,
  sendConversationTypingData,
  updateReceivedConversationTypingData,
  setUserNotificationData,
  updateNotificationDataOnMessageReceived,
  updateNotificationDataOnSeenMessages,
  updateConversationsOnMessageReceived,
  onNewConversationCreated,
  onUserChatDisconnected,
  initiChatConnection,
  setInitiated,
  verifyChatConnection,
  setChatConnectionMode,
  resetCourseChatData,
  chatLoadMoreNotificationData,
  setChatLoadMoreNotificationData,
} = ChatSlice.actions;

export const getChatState = (state: RootState): RootState['chat'] => state.chat;

export default ChatSlice.reducer;
